import { useMutation, useQuery } from '@tanstack/react-query';
import {
  formatDecimal,
  formatMoney,
  formatQuantity,
  type LicenceConfig,
  licenceError,
  type LicenceLinesRequest,
  licenceLight,
  licenceQuote,
  monthsLabel,
  lineDiscountError,
  parseDecimal,
  quoteTotals,
  minimumPax,
  type PriceListResponse,
} from '@k-management/shared';
import { type FormEvent, useEffect, useRef, useState } from 'react';

type LineKey = 'setup' | 'setup_extra' | 'licences' | 'renewal';
/** The discount of a licence line as it is being written: a percentage or the final amount. */
type LineDiscountDraft = { percent: string; final: string };
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { showServerError, type FieldErrors } from '../../lib/form-validation.js';
import { LicenceLightBadge } from './quotes-ui.js';

/**
 * Il configuratore delle licenze (proposta §4): si dice quante licenze
 * servono e il listino fa il resto. Le voci si vedono prima di generare le
 * righe, con il prezzo medio per licenza, come nel foglio di oggi. Lo stesso
 * per il preventivo e per una rata di commessa (2.50): chi lo apre dice dove
 * vanno le righe.
 */
export function LicenceDialog({
  before,
  hasLicences,
  canDiscount,
  priceListPath,
  where,
  onClose,
  onSave,
}: {
  /** Riapre com'era: il configuratore si usa anche per correggere. */
  before: LicenceConfig | null;
  hasLicences: boolean;
  canDiscount: boolean;
  /** The API path of the licence price list. */
  priceListPath: string;
  where: 'preventivo' | 'rata';
  onClose: () => void;
  onSave: (body: LicenceLinesRequest) => Promise<unknown>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const priceList = useQuery({
    queryKey: ['price-list', priceListPath],
    queryFn: () => apiRequest<PriceListResponse>(priceListPath),
    staleTime: 60_000,
  });
  const [pax, setPax] = useState(before ? String(before.pax) : '');
  const [withSetup, setWithSetup] = useState(before?.withSetup ?? true);
  // Setup avanzato (3.11): free text and amount, a line after the setup.
  const [extraOn, setExtraOn] = useState(Boolean(before?.extraSetup));
  const [extraText, setExtraText] = useState(before?.extraSetup?.description ?? '');
  const [extraAmount, setExtraAmount] = useState(
    before?.extraSetup ? formatDecimal(Number(before.extraSetup.amount), 2, 2) : '',
  );
  const [renewal, setRenewal] = useState(before?.renewalDiscount != null);
  const [discount, setDiscount] = useState(before?.renewalDiscount ?? '10');
  // The first period from 1 to 12 months (2.29); the renewal stays a year.
  const [months, setMonths] = useState(before?.months ?? 12);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  const [lineDiscounts, setLineDiscounts] = useState<Partial<Record<LineKey, LineDiscountDraft>>>(
    () =>
      Object.fromEntries(
        Object.entries(before?.lineDiscounts ?? {}).map(([key, value]) => [
          key,
          {
            percent: value.finalAmount ? '' : String(Number(value.discountPercent)),
            final: value.finalAmount ?? '',
          },
        ]),
      ),
  );
  const [discountsReset, setDiscountsReset] = useState(false);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  // Other licences, setup or renewal give other amounts: the discounts of the
  // lines go and are written again (decision of 26/09/2026).
  const config = `${pax}|${withSetup}|${renewal}|${discount}|${months}`;
  const firstConfig = useRef(config);
  useEffect(() => {
    if (config === firstConfig.current) return;
    firstConfig.current = config;
    setLineDiscounts((current) => {
      if (Object.keys(current).length > 0) setDiscountsReset(true);
      return {};
    });
  }, [config]);

  const extraValue = parseDecimal(extraAmount);
  const extraSetup =
    withSetup && extraOn && extraText.trim() && extraValue !== null && extraValue > 0
      ? { description: extraText.trim(), amount: extraValue.toFixed(2) }
      : null;
  const extraIncomplete = withSetup && extraOn && !extraSetup;

  const tiers = priceList.data?.tiers ?? [];
  const number = Number(pax.replace(/[^\d]/g, ''));
  const refusal = pax === '' ? null : licenceError(number, tiers);
  const preview =
    refusal === null && number > 0 && tiers.length > 0
      ? licenceQuote(
          {
            pax: number,
            withSetup,
            renewalDiscount: renewal && canDiscount ? discount : null,
            months,
            extraSetup,
          },
          tiers,
        )
      : null;

  /** Each line worked out with its discount: what it will be in the quote. */
  const discounted = (preview?.lines ?? []).map((line) => {
    const draft = lineDiscounts[line.key];
    const byHand = !!draft && draft.final.trim() !== '';
    const input = {
      quantity: 1,
      unitPrice: line.amount,
      discountPercent: byHand ? 0 : (parseDecimal(draft?.percent ?? '') ?? 0),
      finalAmount: byHand && parseDecimal(draft.final) !== null ? draft.final : null,
      premium: line.premium,
      vatCode: '',
    };
    const net = quoteTotals([input]).net;
    // The setup avanzato is not of the price list: no traffic light (3.11).
    const light =
      priceList.data && !line.mergeUp
        ? licenceLight(line.amount, net, line.premium, priceList.data.thresholds)
        : null;
    return { line, draft, byHand, net, error: lineDiscountError(input), light };
  });
  const listed = discounted.filter((item) => !item.line.mergeUp);
  const overall =
    priceList.data && listed.length > 0
      ? licenceLight(
          listed.reduce((sum, item) => sum + (parseDecimal(item.line.amount) ?? 0), 0),
          listed.reduce((sum, item) => sum + (parseDecimal(item.net) ?? 0), 0),
          listed.reduce((sum, item) => sum + (parseDecimal(item.line.premium) ?? 0), 0),
          priceList.data.thresholds,
        )
      : null;
  const discountedTotal = discounted.reduce((sum, item) => sum + (parseDecimal(item.net) ?? 0), 0);
  const setLineDiscount = (key: LineKey, change: Partial<LineDiscountDraft>) =>
    setLineDiscounts((current) => ({
      ...current,
      [key]: { percent: '', final: '', ...current[key], ...change },
    }));

  const save = useMutation({
    mutationFn: () =>
      onSave({
        pax: number,
        withSetup,
        renewalDiscount: renewal && canDiscount ? discount : null,
        months,
        extraSetup,
        lineDiscounts: canDiscount
          ? Object.fromEntries(
              discounted
                .filter((item) => item.draft)
                .map((item) => [
                  item.line.key,
                  item.byHand
                    ? { discountPercent: 0, finalAmount: item.draft!.final }
                    : { discountPercent: item.draft!.percent || 0, finalAmount: null },
                ]),
            )
          : {},
      }),
    onError: (error: Error) => showServerError(error, setErrors, setFormError),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError('');
    if (refusal || !preview) {
      setErrors({ pax: refusal ?? 'Scrivi quante licenze servono' });
      return;
    }
    if (extraIncomplete) {
      setFormError('Setup avanzato: scrivi la descrizione e l’importo, oppure togli la spunta');
      return;
    }
    const wrong = discounted.find((item) => item.error);
    if (wrong) {
      setFormError(`${wrong.line.label}: ${wrong.error}`);
      return;
    }
    save.mutate();
  };

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <h2>Licenze</h2>
        {priceList.isPending && <p className="muted">Caricamento del listino…</p>}
        {priceList.data && priceList.data.version === null && (
          <p className="notice">
            Il <strong>Listino licenze</strong> non ha una versione attiva: caricalo e attivalo da
            Configurazione → Tabelle.
          </p>
        )}
        {priceList.data?.version !== null && priceList.data && (
          <p className="muted small">
            Listino versione {priceList.data.version}
            {priceList.data.validFrom && `, valido dal ${formatDate(priceList.data.validFrom)}`} ·
            da {formatQuantity(minimumPax(tiers))} a {formatQuantity(tiers.at(-1)?.pax ?? 0)}{' '}
            licenze
          </p>
        )}

        <div className="row">
          <Field label="Licenze (PAX)" error={errors.pax}>
            <input
              className="number"
              inputMode="numeric"
              autoFocus
              value={pax}
              onChange={(event) => setPax(event.target.value)}
              placeholder="350"
            />
          </Field>
          <Field label="Durata" error={errors.months}>
            <select value={months} onChange={(event) => setMonths(Number(event.target.value))}>
              {Array.from({ length: 12 }, (_, index) => 12 - index).map((value) => (
                <option key={value} value={value}>
                  {value === 12 ? '12 mesi (anno intero)' : monthsLabel(value)}
                </option>
              ))}
            </select>
          </Field>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={withSetup}
              onChange={(event) => setWithSetup(event.target.checked)}
            />
            Setup incluso
          </label>
          {canDiscount && (
            <label className="checkbox">
              <input
                type="checkbox"
                checked={renewal}
                onChange={(event) => setRenewal(event.target.checked)}
              />
              Rinnovo 2° anno
            </label>
          )}
          {canDiscount && renewal && (
            <Field label="Sconto sul rinnovo %" error={errors.renewalDiscount}>
              <DecimalInput
                className="number"
                decimals={[0, 2]}
                value={discount}
                onChange={setDiscount}
              />
            </Field>
          )}
        </div>
        {withSetup && (
          <div className="row">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={extraOn}
                onChange={(event) => setExtraOn(event.target.checked)}
              />
              Setup avanzato
            </label>
            {extraOn && (
              <>
                <Field label="Descrizione">
                  <input
                    style={{ minWidth: '22rem' }}
                    value={extraText}
                    maxLength={200}
                    onChange={(event) => setExtraText(event.target.value)}
                    placeholder="Integrazione SSO, import utenti…"
                  />
                </Field>
                <Field label="Importo">
                  <DecimalInput
                    className="number"
                    decimals={[2, 2]}
                    value={extraAmount}
                    onChange={setExtraAmount}
                  />
                </Field>
              </>
            )}
          </div>
        )}
        {withSetup && extraOn && (
          <p className="muted small">
            Testo e importo liberi, fuori listino: nell’offerta per il cliente si somma alla riga
            del setup.
          </p>
        )}

        {refusal && <p className="notice">{refusal}</p>}
        {preview && (
          <>
            <dl className="totals licence-summary">
              <div>
                <dt>Costo setup</dt>
                <dd>{formatMoney(preview.summary.setup)}</dd>
              </div>
              <div>
                <dt>Licenza al mese</dt>
                <dd>{formatMoney(preview.summary.perLicenceMonth)}</dd>
              </div>
              <div>
                <dt>Licenza all’anno</dt>
                <dd>{formatMoney(preview.summary.perLicenceYear)}</dd>
              </div>
              {preview.summary.months !== 12 && (
                <div>
                  <dt>Licenza per {monthsLabel(preview.summary.months)}</dt>
                  <dd>{formatMoney(preview.summary.perLicencePeriod)}</dd>
                </div>
              )}
            </dl>
            {discountsReset && (
              <p className="notice">
                Sono cambiati licenze, setup o rinnovo: gli sconti delle righe sono stati azzerati,
                riscrivili sui nuovi importi.
              </p>
            )}
            <table className="lines">
              <thead>
                <tr>
                  <th>Voce</th>
                  <th className="right">Importo</th>
                  {canDiscount && <th className="right">Sconto %</th>}
                  {canDiscount && <th className="right">Importo finale</th>}
                  {canDiscount && <th className="right">Netto</th>}
                  <th className="right">Sul listino</th>
                  <th className="right">Limite del giallo</th>
                </tr>
              </thead>
              <tbody>
                {discounted.map(({ line, draft, byHand, net, error, light }) => {
                  const gross = parseDecimal(line.amount) ?? 0;
                  const netValue = parseDecimal(net) ?? 0;
                  const room = gross - (parseDecimal(line.premium) ?? 0);
                  return (
                    <tr key={line.key}>
                      <td>
                        {line.label}
                        {Number(line.premium) > 0 && (
                          <small className="muted block">
                            di cui premium {formatMoney(line.premium)}, che non si sconta
                          </small>
                        )}
                        {error && <small className="field-error block">{error}</small>}
                      </td>
                      <td className="right">{formatMoney(line.amount)}</td>
                      {canDiscount && (
                        <td className="right">
                          <DecimalInput
                            className="number small-input"
                            aria-label={`Sconto % di ${line.label}`}
                            decimals={[0, 2]}
                            value={byHand ? '' : (draft?.percent ?? '')}
                            placeholder={
                              byHand && room > 0
                                ? formatDecimal(((gross - netValue) / room) * 100, 0, 2)
                                : '0'
                            }
                            onChange={(value) =>
                              setLineDiscount(line.key, { percent: value, final: '' })
                            }
                          />
                        </td>
                      )}
                      {canDiscount && (
                        <td className="right">
                          <DecimalInput
                            className="number small-input"
                            aria-label={`Importo finale di ${line.label}`}
                            decimals={[2, 2]}
                            value={draft?.final ?? ''}
                            placeholder={formatDecimal(netValue, 2, 2)}
                            onChange={(value) => setLineDiscount(line.key, { final: value })}
                          />
                        </td>
                      )}
                      {canDiscount && <td className="right">{formatMoney(net)}</td>}
                      <td className="right">
                        {light ? (
                          <LicenceLightBadge light={light} />
                        ) : (
                          line.mergeUp && <span className="pill muted">fuori listino</span>
                        )}
                      </td>
                      <td className="right">
                        {light && (
                          <>
                            {formatMoney(light.yellowFloor)}
                            <small className="muted block">
                              {netValue >= (parseDecimal(light.yellowFloor) ?? 0)
                                ? 'sopra il limite'
                                : `mancano ${formatMoney((parseDecimal(light.yellowFloor) ?? 0) - netValue)}`}
                            </small>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <dl className="totals">
              <div>
                <dt>1° anno</dt>
                <dd>{formatMoney(preview.year1)}</dd>
              </div>
              {Number(preview.year2) > 0 && (
                <div>
                  <dt>Rinnovo 2° anno</dt>
                  <dd>{formatMoney(preview.year2)}</dd>
                </div>
              )}
              <div className="grand">
                <dt>Totale fornitura</dt>
                <dd>{formatMoney(preview.total)}</dd>
              </div>
              {Math.round(discountedTotal * 100) !== Math.round(Number(preview.total) * 100) && (
                <div className="grand">
                  <dt>Totale con gli sconti delle righe</dt>
                  <dd>{formatMoney(discountedTotal)}</dd>
                </div>
              )}
            </dl>
            {overall && priceList.data && (
              <p className="licence-overall">
                <span>
                  Licenze rispetto al listino: <LicenceLightBadge light={overall} />
                </span>
                <small className="muted">
                  Verde a prezzo di listino
                  {priceList.data.thresholds.green > 0
                    ? ` o con sconto fino al ${formatDecimal(priceList.data.thresholds.green, 0, 2)} %`
                    : ''}
                  , giallo fino al {formatDecimal(priceList.data.thresholds.yellow, 0, 2)} % di
                  sconto, rosso oltre. Il premium resta fuori; il rinnovo si confronta con il suo
                  listino. Lo sconto sul documento si aggiunge nei Costi previsti.
                </small>
              </p>
            )}
            {preview.warnings.map((warning) => (
              <p key={warning} className="notice">
                {warning}
              </p>
            ))}
            {hasLicences && (
              <p className="muted small">
                Le righe delle licenze già {where === 'rata' ? 'nella rata' : 'nel preventivo'}{' '}
                vengono rifatte; le altre restano.
              </p>
            )}
          </>
        )}

        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={!preview || save.isPending}>
            {save.isPending ? 'Calcolo…' : hasLicences ? 'Rifai le righe' : 'Aggiungi le righe'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
